import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Divider, Stack, Switch} from "@mui/material";
import {useMutation} from "react-query";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {FiasConfig} from "crqlar-react-common/dist/models/integrations/fias/fiasConfig";
import {FiasService} from "crqlar-react-common/dist/services/integrations/fias/fias.service";
import {LoadingButton} from "@mui/lab";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
    fiasConfigData: UseEntityData<FiasConfig>
}

const FiasConfigForm = ({fiasConfigData}: Props) => {

    const {t} = useTranslation()

    const readOnlyUser = useReadOnly()

    const fiasConfig = fiasConfigData.item
    const nameMissing = isRequiredTextMissing(fiasConfigData, fiasConfig.name)
    const setFiasConfig = fiasConfigData.setItem


    //region Handlers

    const checkConnection = useMutation(
        ['checkFiasConfig', fiasConfig],
        async () => {
            let success = false
            try {
                if (fiasConfig.url) {
                    await FiasService.checkConnection(fiasConfig);
                    success = true
                }
            } catch (error) {
            }

            enqueueSnackbar(
                success ? t('Connection successful') : t('Connection failed'),
                {
                    variant: success ? 'success' : 'error',
                    anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
                }
            )
        }
    )

    const updateField = (field: keyof FiasConfig, value: any) => {
        setFiasConfig({...fiasConfig, [field]: value})
    }

    //endregion Handlers

    //region UI

    const credentialsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Integration name')}
                    tooltip={t('Internal label for identifying this FIAS integration configuration.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={fiasConfig.name || ''}
                        error={nameMissing}
                        fullWidth
                        onChange={e => updateField('name', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('URL')}
                    tooltip={t('Host or endpoint used to connect to the FIAS interface.')}
                >
                    <TextField
                        id={'url'}
                        size={'small'}
                        value={fiasConfig.url || ''}
                        fullWidth
                        onChange={e => updateField('url', e.target.value)}
                    />
                </Property>

                <Property
                    title={t('Port')}
                    tooltip={t('Network port used for the FIAS connection.')}
                >
                    <TextField
                        id={'port'}
                        size={'small'}
                        value={fiasConfig.port || ''}
                        fullWidth
                        onChange={e => updateField('port', parseInt(e.target.value))}
                        type={'number'}
                    />
                </Property>

                <Stack direction={'row'} justifyContent={'flex-start'}>
                    <LoadingButton
                        disabled={readOnlyUser}
                        loading={checkConnection.isLoading}
                        onClick={() => checkConnection.mutate()}
                        variant={'outlined'}
                    >
                        {t('Test connection')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Stack>
    )

    const configurationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Send link records (LR)')}
                    tooltip={t('Sends FIAS link records when this interface requires them.')}
                >
                    <Switch
                        id={'enabled'}
                        checked={fiasConfig.sendLinkRecords || false}
                        onChange={(e) => updateField('sendLinkRecords', e.target.checked)}
                    />
                </Property>

                <Property
                    title={t('Enabled')}
                    tooltip={t('Turns the FIAS integration on or off.')}
                >
                    <Switch
                        id={'enabled'}
                        checked={fiasConfig.enabled || false}
                        onChange={(e) => updateField('enabled', e.target.checked)}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    //endregion UI

    return (
        <Stack
            sx={{
                width: '100%',
                gap: 6,
            }}
        >
            {credentialsSection}
            <Divider/>
            {configurationSection}
        </Stack>
    )

}

export default FiasConfigForm;
